/**
 * CHORUS - Multi-AI Chat Application
 * Entry Point
 */

import './styles/tokens.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/mission-control.css';
import './styles/voice.css';

import { renderApp, updateStreamingMessages } from './components/App.js';
import { store, setMemoryItems, setUsageData } from './lib/appState.js';
import { loadStoredConversations } from './lib/chat.js';
import { loadStoredDocuments } from './lib/documents.js';
import { providers } from './providers/registry.js';
import { initAuth, getSession } from './lib/auth.js';
import { renderLogin } from './components/Login.js';
import { renderPasswordGate, cleanupPasswordGate } from './components/PasswordGate.js';
import { isGatePassed } from './lib/passwordGate.js';
import { initTheme } from './components/Settings.js';
import { loadMemory } from './lib/memory.js';
import { loadUsage } from './lib/usage.js';

const app = document.querySelector('#app');

// Initialize theme before render to avoid flash
initTheme();

// Password gate — check gate before authentication
async function boot() {
  // Check password gate first
  if (!isGatePassed()) {
    renderPasswordGate(app, () => {
      // On successful gate pass, proceed to auth
      checkAuthAndStart();
    });
    return;
  }
  checkAuthAndStart();
}

// Auth gate + app start
async function checkAuthAndStart() {
  const session = await initAuth();
  if (!session) {
    renderLogin(app, () => {
      // On successful login, render the app
      startApp();
    });
    return;
  }
  startApp();
}

function startApp() {
  // Initial render
  renderApp(app);

  // Subscribe to state changes for re-rendering
  let renderPending = false;
  store.subscribe((state, prevState, changedPaths) => {
    // During streaming, only update message content (no full re-render)
    if (state.chat.isStreaming && changedPaths.every(p => p.startsWith('conversations') || p.startsWith('agentConversations'))) {
      requestAnimationFrame(() => updateStreamingMessages(app));
      return;
    }

    // Routing suggestion changes — update DOM directly without full re-render
    // This prevents destroying the textarea (and losing typed text) on every keystroke
    if (changedPaths.every(p => p.startsWith('routingSuggestion'))) {
      requestAnimationFrame(() => {
        const container = app.querySelector('.routing-suggestion-container');
        if (container) {
          const suggestion = state.routingSuggestion;
          if (!suggestion || state.parallelMode.enabled) {
            container.innerHTML = '';
          } else {
            container.innerHTML = `
              <div class="routing-suggestion animate-fade-in-up">
                <span class="routing-suggestion-icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg></span>
                <span class="routing-suggestion-text">Suggested: <strong>${suggestion.reason}</strong></span>
                <button class="routing-suggestion-use" data-action="use-routing" data-provider="${suggestion.provider}" data-model="${suggestion.model}">Use</button>
                <button class="routing-suggestion-dismiss" data-action="dismiss-routing"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
              </div>
            `;
          }
        }
      });
      return;
    }

    const shouldRerender = changedPaths.some(path =>
      path.startsWith('ui.') ||
      path.startsWith('chat.') ||
      path.startsWith('conversations') ||
      path.startsWith('agentConversations') ||
      path.startsWith('parallelMode.') ||
      path.startsWith('knowledgeBase') ||
      path.startsWith('attachedFiles') ||
      path.startsWith('routingSuggestion') ||
      path.startsWith('memory') ||
      path.startsWith('followUps') ||
      path.startsWith('usage') ||
      path.startsWith('settings')
    );

    if (shouldRerender && !renderPending) {
      renderPending = true;
      requestAnimationFrame(() => {
        renderApp(app);
        renderPending = false;
      });
    }
  });

  // Load persisted conversations
  loadStoredConversations();

  // Load stored KB documents
  loadStoredDocuments();

  // Load memory and usage
  loadMemory().then(items => {
    setMemoryItems(items);
  });
  loadUsage().then(usage => {
    setUsageData({
      currentConv: { inputTokens: 0, outputTokens: 0, cost: 0 },
      total: usage.total,
    });
  });

  // Load available models from Worker API
  providers.loadFromWorker().then(() => {
    renderApp(app);
  });

  // Register service worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  }

  console.log('Chorus v5.0 initialized');
}

// Boot the app
boot();
